import axios from "axios";
import { from } from "rxjs";
import oidcService from "./oidcService";
import linkSession from "./linkSessionService";
import translateService from "./translateService";
import stateService from "./stateService";
import { stateEvents } from "../models/states";

// signature-gateway client (face runbook F3.4): its own axios instance, so the
// global interceptors (ObjectResponse unwrapping, 401 sign-out, error modal)
// never run for gateway calls. Same Bearer as for validation-api; one retry
// after a 401 with a refreshed token, then the caller handles the error.
// A tab holding a document link sends a share link's token (never the
// Keycloak one) and never a public view's: the gateway refuses those.
// (exported for the signer management client, signerManagementService)
export const gateway = axios.create({ baseURL: globalConfig.sign_api_URL });

const bearer = async () => {
  if (linkSession.has()) {
    if (!linkSession.isShareLink()) return null;
    const token = await linkSession.token();
    return token ? `Bearer ${token}` : null;
  }
  const user = await oidcService.ensureFreshUser(30).catch(() => null);
  return user && user.access_token ? `Bearer ${user.access_token}` : null;
};

gateway.interceptors.request.use(async (config) => {
  // extra headers per environment, e.g. ngrok's
  // `ngrok-skip-browser-warning` (else its free tier answers with an HTML page)
  config.headers = { ...(globalConfig.sign_api_headers || {}), ...(config.headers || {}) };
  // Problem JSON `detail` in the app's language, not the browser's
  config.headers["Accept-Language"] = translateService.key || "ka";
  const token = await bearer();
  if (token) config.headers.Authorization = token;
  return config;
});

gateway.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const status = error.response && error.response.status;
    // the gateway relays the B2B session's two answers (sessionService reacts)
    const body = error.response && error.response.data;
    const type = body && typeof body === "object" && String(body.type || "").split("/").pop();
    if (type === "ORGANIZATION_MEMBERSHIP_REQUIRED") stateService.event(stateEvents.organizationMembershipLost);
    else if (type === "ORGANIZATIONS_UNAVAILABLE") stateService.event(stateEvents.organizationsUnavailable);
    if (status !== 401 || config._retried) return Promise.reject(error);

    // a share link: exchange the link again, retry once (a refusal shows
    // its page); a second 401 is the caller's error to show
    if (linkSession.isShareLink()) {
      config._retried = true;
      const slot = await linkSession.renew();
      if (!slot) return Promise.reject(error);
      config.headers.Authorization = `Bearer ${slot.accessToken}`;
      return gateway(config);
    }

    if (linkSession.has() || !oidcService.currentUser()) return Promise.reject(error);
    config._retried = true;
    const user = await oidcService.refreshSingleFlight().catch(() => null);
    if (!user) return Promise.reject(error);
    config.headers.Authorization = `Bearer ${user.access_token}`;
    return gateway(config);
  }
);

// a blob download's refusal arrives as a Blob: read it as the problem JSON
export const blobProblem = async (e) => {
  const data = e && e.response && e.response.data;
  if (data && typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      e.response.data = JSON.parse(await data.text());
    } catch (x) {
      e.response.data = null;
    }
  }
  throw e;
};

// save a downloaded file under the given name
export const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// the file's own name when the answer lets the page read it
// (Content-Disposition is readable cross-origin only when the gateway
// exposes it), else the fallback
const fileNameOf = (res, fallback) => {
  const header = res && res.headers && res.headers["content-disposition"];
  const m = header && /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return m ? decodeURIComponent(m[1]) : fallback;
};

const EVIDENCE_EXTENSION = { "image/jpeg": "jpg", "video/mp4": "mp4", "application/pdf": "pdf" };

// The document code a face signing's consent starts with, from the hex digest
// of the document's bytes (the clientHash's algorithm): its first 16 digits,
// upper-case, in four groups of four ("2dac8b8b..." -> "2DAC-8B8B-CBEC-FECB")
export const documentCode = (hex) =>
  String(hex).slice(0, 16).toUpperCase().match(/.{4}/g).join("-");

// signatory authentications this frontend can carry out (PRESHARED_KEY is
// the decoupled agent's key, never in the browser; CREDENTIAL comes with the
// second cut, face runbook M1–M4; EID is the Unitool application's)
export const BROWSER_AUTH_TYPES = ["FACE", "EID"];

// digest algorithms WebCrypto can compute that are acceptable for a signature
// (never SHA-1); names as WebCrypto and the gateway spell them
const WEBCRYPTO_DIGESTS = ["SHA-256", "SHA-384", "SHA-512"];

class SignService {
  // signers the caller may sign with; type and workspaceId are optional filters
  getSigners = (params) => from(gateway.get("api/remotesign/signer", { params }));

  // The sharer's signers of a workspace as the gateway signed them (SG6), for
  // the share dialog: the body as raw text - the signature covers its exact
  // bytes, so it is forwarded unchanged and only a copy is parsed - and the
  // X-JWS-Signature header. -> { body, signature, listing (parsed copy) }
  getSignerListing = (workspaceId) =>
    from(
      gateway
        .get("api/remotesign/signer", {
          params: { workspaceId },
          responseType: "text",
          transformResponse: [(data) => data],
        })
        .then((res) => {
          let listing = null;
          try {
            listing = JSON.parse(res.data);
          } catch (e) {
            listing = null;
          }
          return { body: res.data, signature: res.headers["x-jws-signature"] || null, listing };
        })
    );

  // What the caller may sign a document with (SG6): { signers (RemoteSigner,
  // by uuid), kinds [{kind, label, supportsLimits}] }, from the document's
  // signing-options read with the caller's token - through a share exactly
  // what it grants, else the caller's own signers in the document's workspace
  // and the app-based kinds; only what the document's rights allow. A share
  // link may call it for its own document; a public view never.
  getDocumentSigners = (docId) => from(gateway.get("api/remotesign/signer", { params: { docId } }));

  // A document's signing processes the caller may list (the owner and the
  // workspace's owner all, a sharer those through their shares, anyone their
  // own), newest first -> { docId, beingSigned {since} | null, processes:
  // [{ processId, status, ended, failure, shareId, signerKind, authType,
  // createdAt, requesterName, byCaller, throughLink, signatoryRequired,
  // holdsDocument }], pageInfo {next} }. Signed in only: a link is refused.
  getDocumentProcesses = (docId, cursor) =>
    from(
      gateway.get(`api/remotesign/document/${docId}/process`, {
        params: cursor ? { cursor } : { "page-size": 100 },
      })
    );

  // the document's SIGNED processes the caller may list (their own at least),
  // one page: enough to tell whether a cloud signing tool signed it
  getSignedDocumentProcesses = (docId) =>
    from(
      gateway.get(`api/remotesign/document/${docId}/process`, {
        params: { status: "SIGNED", "page-size": 100 },
      })
    );

  // One evidence file a signing kept, for a dispute - kind photo, video or
  // pdf, as the process's verificationEvidenceDownloads offers them; the
  // signatory's or the document owner's, signed in -> { blob, name }. The
  // file is the person's biometric data: shown or saved, never kept or
  // logged here.
  getVerificationEvidence = (processId, kind) =>
    from(
      gateway
        .get(`api/remotesign/process/${processId}/verification-evidence/${kind}`, { responseType: "blob" })
        .then((res) => {
          const type = String((res.headers && res.headers["content-type"]) || "").split(";")[0];
          const extension = EVIDENCE_EXTENSION[type] || (kind === "photo" ? "jpg" : kind === "video" ? "mp4" : "pdf");
          return { blob: res.data, name: fileNameOf(res, `${processId}-${kind}.${extension}`) };
        })
        .catch(blobProblem)
    );

  // one signer; with dynamicData the live stats of its decoupled agents
  getSigner = (id, dynamicData = false) =>
    from(gateway.get(`api/remotesign/signer/${id}`, { params: { "dynamic-data": dynamicData } }));

  // The gateway relays the caller's token to validation-api while it works
  // (the share's grant, the signatory check): before /finish and before the
  // call that starts an eID process, the token must have at least two minutes
  // left - a Keycloak token is refreshed, a link's token exchanged again.
  renewFor = async (seconds = 120) => {
    if (linkSession.has()) await linkSession.token(seconds * 1000);
    else await oidcService.ensureFreshUser(seconds).catch(() => null);
  };

  // { docId, workspaceId (both exposed ids), signerId (UUID) | signerKind, clientHash,
  //   clientHashAlgorithm, preferredAuthType? } → 201 SigningProcess
  // { processId, documentHash, documentHashAlgorithm, status, signer,
  // authType?, visualSignature? } with the process version in the ETag
  // header (res.headers.etag). authType is absent when the preferred one is
  // missing or unsupported: pick with PATCH. Never a numeric signer id.
  createProcess = (body) =>
    from(this.renewFor().then(() => gateway.post("api/remotesign/process", body)));

  getProcess = (processId) => from(gateway.get(`api/remotesign/process/${processId}`));

  // starts (or, while one is open, returns) the signatory's verification; no
  // body, language from Accept-Language → 201 {widgetUrl, expiresAt,
  // attempt, maxAttempts}; widgetUrl carries the session token: never log it
  startVerification = (processId) =>
    from(gateway.post(`api/remotesign/process/${processId}/verification`));

  // what came of the verification so far → the process (status, verification,
  // rejectReason) with its ETag. Poll ~3 s while VERIFICATION_STARTED and at
  // once on DONE; the gateway asks Identomat at most every 10 s. 500 = keep
  // polling; 409 VERIFICATION_NOT_STARTED = nothing to check.
  checkVerification = (processId) =>
    from(gateway.post(`api/remotesign/process/${processId}/verification/check`));

  // places the visible signature: {page, x, y} in points, top-left of the box
  // from the page's top-left, pages from 1; null for a fixed position (the
  // gateway takes it from the signing options). If-Match: the last ETag.
  // → the process, PLACED. 409 POSITION_FIXED, 412 PROCESS_CHANGED.
  placeSignature = (processId, etag, placement) =>
    from(
      gateway.put(`api/remotesign/process/${processId}/placement`, placement || {}, {
        headers: { "If-Match": etag },
      })
    );

  // signs: from PLACED, or from VERIFIED when the mark is not mandatory; no
  // body → 202, SIGNING; poll GET until SIGNED or SIGNING_FAILED
  finish = (processId) =>
    from(this.renewFor().then(() => gateway.post(`api/remotesign/process/${processId}/finish`)));

  // Georgian eID (authType EID): hands out the link that opens Unitool on
  // this device → the process, EID_LAUNCHED, with eid {launchUrl, expiresAt}
  // (5 minutes; calling again hands out a new one). The gateway reads the
  // share's grant with the relayed token: renew it first (task 20).
  launchEid = (processId) =>
    from(this.renewFor().then(() => gateway.post(`api/remotesign/process/${processId}/eid`)));

  // → CANCELLED (final); refused once SIGNING or later
  cancel = (processId) => from(gateway.post(`api/remotesign/process/${processId}/cancel`));

  // sets the signatory authentication; optimistic locking with the last ETag
  // (412 PROCESS_CHANGED when stale; 400 AUTH_TYPE_NOT_SUPPORTED)
  setAuthType = (processId, etag, authType) =>
    from(
      gateway.patch(
        `api/remotesign/process/${processId}`,
        { authType },
        { headers: { "If-Match": etag } }
      )
    );

  // the first algorithm of signing-options.allowedClientHashAlgorithms (in the
  // backend's order of preference) that the browser can compute; null if none
  pickHashAlgorithm = (allowed) => {
    for (const name of allowed || []) {
      const match = WEBCRYPTO_DIGESTS.find((a) => a === String(name).toUpperCase());
      if (match) return match;
    }
    return null;
  };

  // lowercase hex digest of the bytes (ArrayBuffer or typed array)
  digestHex = async (algorithm, bytes) => {
    const digest = await window.crypto.subtle.digest(algorithm, bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  // a problem body of the service's own: one with a `type`. Spring's own
  // refusals (a malformed id: "Failed to convert 'id' with value ...") carry
  // none, and their title and detail are never shown
  isOwnProblem = (body) => !!body && typeof body === "object" && !!body.type && body.type !== "about:blank";

  // the gateway's refusal code: its Problem `type` ends in it
  // (…/VERIFICATION_ATTEMPTS_EXHAUSTED); null for other errors
  problemCode = (error) => {
    const body = error && error.response && error.response.data;
    const type = body && typeof body === "object" && body.type;
    return type ? String(type).split(/[/#]/).pop() : null;
  };

  // text for the global error window: the gateway's application/problem+json
  // detail (or title) when it sent one, else the fallback
  problemMessage = (error, fallback) => {
    const body = error && error.response && error.response.data;
    return (this.isOwnProblem(body) && (body.detail || body.title)) || fallback;
  };
}

export default new SignService();
